/**
 * Opens the full-screen "Album view" overlay from anywhere (rendered in app.component).
 * Mirrors OpenBindersOverlayService pattern.
 */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export interface AlbumOverlayCallbacks {
  /** Called when overlay closes; `activePageIndex` is the last viewed page. */
  onDismiss?: (result: { activePageIndex: number }) => void;
  /** User tapped a sticker; parent should open sticker detail above the album. */
  onStickerClick?: (itemId: string) => void;
  /** User tapped “Order album” / buy CTA below the page nav. */
  onBuyAlbum?: () => void;
}

export interface AlbumOverlayConfig {
  agentId: string;
  collectionId: string;
  userCollectionId: string;
  tradesEnabled: boolean;
  /** Pre-loaded pages from `loadAlbumPages`. */
  albumPagesSorted: any[];
  /** Sections map from `loadAlbumPages`. */
  albumSections: any[][];
  /** @deprecated Album sticker images use grid URLs (`icon_path` / placeholders); kept for callers. */
  stickerUrlPrefix?: string;
  iconLocationPrefix: string;
  /** Placeholder filenames under `icon_location_prefix` (same as items grid when `!icon_path`). */
  noStickerImgP: string;
  noStickerImgL: string;
  /** Owned item map: `item_id -> true` for items where Tenho === true/1. */
  ownedItemIds: Set<string>;
  /**
   * Flat list aligned with user-collection `userColectionFullList`: `item_id`, `Tenho`,
   * and optional `icon_path` for locked preview (same as `getStickerUrlNew`).
   */
  fullItemList: { item_id: string; Tenho: any; icon_path?: string; picture_path?: string }[];
  /** When coming from gesture/slide view, jump to this item's page on open. */
  focusItemId?: string;
  /** Starting page index (used if focusItemId not provided). */
  initialPageIndex?: number;
  /** When true, show the buy-album control that peeks from behind the bottom nav. */
  showBuyAlbumButton?: boolean;
  /** When true, collection is 100% complete — buy CTA plays a colorful entry animation. */
  collectionComplete?: boolean;
  callbacks?: AlbumOverlayCallbacks;
}

export interface AlbumOverlayState extends AlbumOverlayConfig {
  isOpen: boolean;
}

const EMPTY_STATE: AlbumOverlayState = {
  isOpen: false,
  agentId: '',
  collectionId: '',
  userCollectionId: '',
  tradesEnabled: false,
  albumPagesSorted: [],
  albumSections: [],
  stickerUrlPrefix: '',
  iconLocationPrefix: '',
  noStickerImgP: '',
  noStickerImgL: '',
  ownedItemIds: new Set(),
  fullItemList: [],
  focusItemId: undefined,
  initialPageIndex: 0,
  showBuyAlbumButton: false,
  collectionComplete: false,
  callbacks: undefined,
};

@Injectable({
  providedIn: 'root',
})
export class AlbumOverlayService {
  private readonly stateSubject = new BehaviorSubject<AlbumOverlayState>({ ...EMPTY_STATE });
  private readonly hardwareBack = new Subject<void>();
  /**
   * Emits when the app (hardware back / iOS edge swipe) should perform the same close
   * as the in-overlay dismiss so the correct `activePageIndex` is reported.
   */
  readonly hardwareBack$ = this.hardwareBack.asObservable();

  readonly state$: Observable<AlbumOverlayState> = this.stateSubject.asObservable();

  open(config: AlbumOverlayConfig): void {
    this.stateSubject.next({ ...EMPTY_STATE, ...config, isOpen: true });
  }

  /**
   * Merges new album snapshot into the current overlay while it stays open (e.g. album
   * pages finished loading after the user opened the overlay with empty data).
   */
  patchIfOpen(partial: Partial<AlbumOverlayConfig>): void {
    const cur = this.stateSubject.value;
    if (!cur.isOpen) return;
    this.stateSubject.next({ ...cur, ...partial });
  }

  close(): void {
    this.stateSubject.next({ ...this.stateSubject.value, isOpen: false });
  }

  requestHardwareBackClose(): void {
    if (this.isOpen) {
      this.hardwareBack.next();
    }
  }

  get isOpen(): boolean {
    return this.stateSubject.value.isOpen;
  }

  get callbacks(): AlbumOverlayCallbacks | undefined {
    return this.stateSubject.value.callbacks;
  }
}
